import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'
import type { MessageContext } from '@/lib/whatsapp'
import type { WhatsAppOrder } from '@/components/shop/whatsapp-link'

import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { ArrowRight, ChevronLeft, ChevronRight, Rotate3d, ShoppingBag, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatIDR } from '@/lib/format'
import { buildProductMessage } from '@/lib/whatsapp'
import { useShop } from '@/components/providers/shop-provider'
import { ProductImage } from '@/components/shop/product-image'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'

import {
    ColorSwatches,
    ProductBadges,
    StockBadge,
    WishlistButton
} from '@/components/shop/shop-bits'

interface BoutiqueQuickViewProps {
    product: Product | null
    // True when the product was picked up from a shelf as a 3D doll (false for catalogue products).
    inScene: boolean
    open: boolean
    colorIndex: number
    onColorChange: (index: number) => void
    onClose: () => void
    onPrevious: (() => void) | null
    onNext: (() => void) | null
}

// The product card that slides in when a doll is picked up in the 3D boutique.
export const BoutiqueQuickView: FC<BoutiqueQuickViewProps> = ({
    product,
    inScene,
    open,
    colorIndex,
    onColorChange,
    onClose,
    onPrevious,
    onNext
}): ReactNode => {
    const { addToCart } = useShop()
    const panel = useRef<HTMLDivElement>(null)
    const closeButton = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (open) closeButton.current?.focus({ preventScroll: true })
    }, [open, product?.id])

    const color = product?.colors[Math.min(colorIndex, Math.max(0, (product?.colors.length ?? 1) - 1))] ?? null
    const soldOut = product ? !product.madeToOrder && product.stock <= 0 : false

    const message = useCallback((context: MessageContext) => {
        if (!product) return ''

        return buildProductMessage(context, {
            name: product.name,
            slug: product.slug,
            price: product.price,
            quantity: 1,
            colorLabel: color?.label ?? null,
            madeToOrder: product.madeToOrder
        })
    }, [product, color])

    const order = useMemo<WhatsAppOrder>(() => ({
        source: 'produk',
        items: product ? [{ productId: product.id, quantity: 1, colorLabel: color?.label ?? null }] : []
    }), [product, color])

    const handleAdd = () => {
        if (!product) return

        addToCart({
            productId: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            image: product.image,
            designKey: product.designKey,
            colorLabel: color?.label ?? null,
            colorHex: color?.hex ?? null,
            accentColor: product.accentColor,
            maxQuantity: product.madeToOrder ? null : product.stock
        })
    }

    return (
        <aside
            ref={panel}
            aria-label={product ? `Detail ${product.name}` : 'Detail produk'}
            inert={!open}
            className={cn(
                'absolute z-30 flex flex-col overflow-hidden border border-white/60 bg-cream-50/92 shadow-[0_30px_80px_-30px_rgba(58,40,30,0.55)] backdrop-blur-xl transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                'inset-x-2 bottom-2 max-h-[min(60svh,520px)] rounded-[28px]',
                'lg:inset-x-auto lg:bottom-6 lg:right-6 lg:top-24 lg:max-h-none lg:w-[400px]',
                open ? 'translate-y-0 opacity-100 lg:translate-x-0' : 'pointer-events-none translate-y-[110%] opacity-0 lg:translate-x-[115%] lg:translate-y-0'
            )}
        >
            {product && (
                <>
                    <div className='flex items-center justify-between gap-2 border-b border-border/70 px-5 py-3'>
                        <div className='flex items-center gap-1'>
                            {inScene && (
                                <>
                                    <button
                                        type='button'
                                        onClick={onPrevious ?? undefined}
                                        disabled={!onPrevious}
                                        aria-label='Boneka sebelumnya di rak ini'
                                        className='grid size-9 place-items-center rounded-full text-cocoa-700 transition hover:bg-muted disabled:opacity-30'
                                    >
                                        <ChevronLeft className='size-5' />
                                    </button>
                                    <button
                                        type='button'
                                        onClick={onNext ?? undefined}
                                        disabled={!onNext}
                                        aria-label='Boneka berikutnya di rak ini'
                                        className='grid size-9 place-items-center rounded-full text-cocoa-700 transition hover:bg-muted disabled:opacity-30'
                                    >
                                        <ChevronRight className='size-5' />
                                    </button>
                                </>
                            )}
                        </div>
                        {inScene && (
                            <p className='hidden items-center gap-1.5 text-xs font-semibold text-cocoa-500 sm:flex'>
                                <Rotate3d className='size-4' />
                                Geser boneka untuk memutarnya
                            </p>
                        )}
                        <button
                            ref={closeButton}
                            type='button'
                            onClick={onClose}
                            aria-label={inScene ? 'Kembalikan boneka ke rak' : 'Tutup detail produk'}
                            className='grid size-9 place-items-center rounded-full bg-white text-cocoa-700 shadow-sm transition hover:bg-blush-50'
                        >
                            <X className='size-5' />
                        </button>
                    </div>

                    <div className='flex-1 overflow-y-auto overscroll-contain px-5 pb-5 pt-4'>
                        <div className='flex gap-4 lg:flex-col'>
                            <ProductImage
                                src={product.image}
                                alt={product.imageAlt ?? `${product.name}, boneka rajut handmade`}
                                designKey={product.designKey}
                                mainColor={color?.hex ?? null}
                                accentColor={product.accentColor}
                                className='aspect-[4/5] w-28 shrink-0 rounded-2xl sm:w-32 lg:aspect-[5/4] lg:w-full'
                            />

                            <div className='min-w-0 flex-1'>
                                {product.category && (
                                    <p className='text-xs font-bold uppercase tracking-[0.18em] text-blush-600'>
                                        <span data-mdlfy-cms={`categories:${product.category.id}:name`}>
                                            {product.category.name}
                                        </span>
                                    </p>
                                )}
                                <h2 className='mt-1.5 font-heading text-2xl leading-tight text-cocoa-900 sm:text-[1.7rem]'>
                                    <span data-mdlfy-cms={`products:${product.id}:name`}>
                                        {product.name}
                                    </span>
                                </h2>
                                <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-1'>
                                    <p className='text-lg font-bold text-cocoa-800'>
                                        {formatIDR(product.price)}
                                    </p>
                                    <StockBadge stock={product.stock} madeToOrder={product.madeToOrder} className='text-xs' />
                                </div>
                                <ProductBadges product={product} className='mt-2' badgeClassName='border border-cocoa-700/10 shadow-none' />
                                {product.tagline && (
                                    <p className='mt-2.5 text-sm leading-relaxed text-muted-foreground'>
                                        <span data-mdlfy-cms={`products:${product.id}:tagline`}>
                                            {product.tagline}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>

                        {product.colors.length > 1 && (
                            <div className='mt-5'>
                                <ColorSwatches colors={product.colors} value={colorIndex} onChange={onColorChange} size='sm' />
                            </div>
                        )}

                        <div className='mt-5 grid grid-cols-[1fr_auto] gap-2.5'>
                            <button
                                type='button'
                                onClick={handleAdd}
                                disabled={soldOut}
                                className='inline-flex h-12 items-center justify-center gap-2 rounded-full bg-cocoa-800 px-5 text-sm font-semibold text-cream-50 transition hover:-translate-y-0.5 hover:bg-cocoa-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0'
                            >
                                <ShoppingBag className='size-5' />
                                {soldOut ? 'Stok Habis' : 'Tambah ke Keranjang'}
                            </button>
                            <WishlistButton
                                item={{
                                    productId: product.id,
                                    slug: product.slug,
                                    name: product.name,
                                    price: product.price,
                                    image: product.image,
                                    designKey: product.designKey,
                                    colorHex: product.colors[0]?.hex ?? null,
                                    accentColor: product.accentColor
                                }}
                                className='size-12 border border-border bg-card'
                            />
                            <WhatsAppLink message={message} order={order} disabled={soldOut} variant='outline' className='col-span-2'>
                                Pesan Sekarang
                            </WhatsAppLink>
                        </div>

                        <Link
                            href={`/products/${product.slug}`}
                            className='group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cocoa-700 underline-offset-4 hover:underline'
                        >
                            {product.designKey || product.model ? 'Lihat Detail Produk dan putar 360°' : 'Lihat Detail Produk'}
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </Link>
                    </div>
                </>
            )}
        </aside>
    )
}
